// frontend/src/marketing/blocks.jsx
//
// Reusable marketing content blocks composed from the ui/ primitives.
import React from "react";
import { Disclosure } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import PropTypes from "prop-types";
import { Container, Section, Eyebrow, Button, cx } from "./ui";

/* ------------------------------------------------------------------ FeatureGrid */
export function FeatureGrid({ eyebrow, title, subtitle, features, columns }) {
  const cols = columns === 2 ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3";
  return (
    <Section>
      <div className="mx-auto max-w-2xl text-center">
        {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
        <h2 className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
          {title}
        </h2>
        {subtitle && <p className="mt-4 text-lg text-gray-600">{subtitle}</p>}
      </div>
      <div className={cx("mt-14 grid grid-cols-1 gap-8", cols)}>
        {features.map((f) => (
          <div key={f.title} className="rounded-2xl bg-white p-6 ring-1 ring-gray-200">
            {f.icon && (
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
                <f.icon className="h-6 w-6" aria-hidden="true" />
              </div>
            )}
            <h3 className="mt-4 text-lg font-semibold text-gray-900">{f.title}</h3>
            <p className="mt-2 text-sm leading-6 text-gray-600">{f.description}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}
FeatureGrid.propTypes = {
  eyebrow: PropTypes.string,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  features: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      icon: PropTypes.elementType,
    }),
  ).isRequired,
  columns: PropTypes.oneOf([2, 3]),
};
FeatureGrid.defaultProps = { eyebrow: undefined, subtitle: undefined, columns: 3 };

/* --------------------------------------------------------------------- LogoCloud */
export function LogoCloud({ heading, logos }) {
  return (
    <Section className="py-12 sm:py-14">
      <p className="text-center text-sm font-medium text-gray-500">{heading}</p>
      <div className="mt-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-6 opacity-70">
        {logos.map((name) => (
          <span key={name} className="text-lg font-semibold tracking-tight text-gray-400">
            {name}
          </span>
        ))}
      </div>
      <p className="mt-6 text-center text-xs text-gray-400">
        Placeholder logos — swap for real customer marks before launch.
      </p>
    </Section>
  );
}
LogoCloud.propTypes = {
  heading: PropTypes.string,
  logos: PropTypes.arrayOf(PropTypes.string).isRequired,
};
LogoCloud.defaultProps = { heading: "Trusted by teams shipping campaigns every day" };

/* ---------------------------------------------------------------------- StatStrip */
export function StatStrip({ stats }) {
  return (
    <div className="border-y border-gray-200 bg-gray-50">
      <Container>
        <dl className="grid grid-cols-2 gap-y-8 py-12 sm:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <dt className="text-sm font-medium text-gray-500">{s.label}</dt>
              <dd className="mt-1 text-3xl font-bold tracking-tight text-gray-900">{s.value}</dd>
            </div>
          ))}
        </dl>
      </Container>
    </div>
  );
}
StatStrip.propTypes = {
  stats: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, value: PropTypes.string }),
  ).isRequired,
};

/* --------------------------------------------------------------------- CTASection */
export function CTASection({ title, subtitle, primary, secondary }) {
  return (
    <Section>
      <div className="relative overflow-hidden rounded-3xl bg-brand-600 px-6 py-16 text-center sm:px-16">
        <h2 className="mx-auto max-w-2xl text-3xl font-bold tracking-tight text-white sm:text-4xl">
          {title}
        </h2>
        {subtitle && (
          <p className="mx-auto mt-4 max-w-xl text-lg text-brand-100">{subtitle}</p>
        )}
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Button to={primary.to} size="lg" variant="secondary">
            {primary.label}
          </Button>
          {secondary && (
            <Button
              to={secondary.to}
              size="lg"
              variant="ghost"
              className="!text-white hover:!bg-brand-500"
            >
              {secondary.label}
            </Button>
          )}
        </div>
      </div>
    </Section>
  );
}
CTASection.propTypes = {
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  primary: PropTypes.shape({ to: PropTypes.string, label: PropTypes.string }).isRequired,
  secondary: PropTypes.shape({ to: PropTypes.string, label: PropTypes.string }),
};
CTASection.defaultProps = { subtitle: undefined, secondary: undefined };

/* ------------------------------------------------------------------- FAQAccordion */
export function FAQAccordion({ items }) {
  return (
    <dl className="mx-auto max-w-3xl divide-y divide-gray-200">
      {items.map((item) => (
        <Disclosure as="div" key={item.q} className="py-5">
          {({ open }) => (
            <>
              <dt>
                <Disclosure.Button className="flex w-full items-start justify-between text-left text-gray-900">
                  <span className="text-base font-semibold">{item.q}</span>
                  <ChevronDownIcon
                    className={cx(
                      "ml-4 h-5 w-5 flex-none text-gray-400 transition-transform",
                      open && "rotate-180",
                    )}
                    aria-hidden="true"
                  />
                </Disclosure.Button>
              </dt>
              <Disclosure.Panel as="dd" className="mt-3 pr-10 text-sm leading-6 text-gray-600">
                {item.a}
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      ))}
    </dl>
  );
}
FAQAccordion.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({ q: PropTypes.string, a: PropTypes.node }),
  ).isRequired,
};
